import { FieldValue, increment } from 'firebase/firestore';
import { uniq } from 'lodash';
import uniqid from 'uniqid';
import {
  Dish,
  DishComponent,
  Order,
  OrderItem,
  OrderLog,
  Product,
  SelfOrderPoint,
  SemiProduct,
  Store,
  StoreLog,
  Worker,
} from '../../apiTypes';
import { createBatch } from '../../utils/api';
import { canClose } from '../../utils/canCloseOrder';
import { concatComponents } from '../../utils/concatComponents';
import { getOrderItemCost } from '../../utils/getPriceByUnit';
import { isNotNull } from '../../utils/isNotNull';
import { Map } from '../../utils/map';

type Props = {
  cafeSlug: string;
  order: DbDocument<Order>;
  worker: DbDocument<Worker>;
  item: OrderItem;
  dish: DbDocument<Dish>;
  productsMap: Map<DbDocument<Product>>;
  semiProductsMap: Map<DbDocument<SemiProduct>>;
  selfOrderPointsMap: Map<DbDocument<SelfOrderPoint>>;
};

export const changeOrderItem = async ({
  order,
  worker,
  item,
  cafeSlug,
  dish,
  productsMap,
  semiProductsMap,
  selfOrderPointsMap,
}: Props) => {
  const canEdit =
    (worker?.credentials.create_and_edit_own_orders && order.workerId === worker.id) ||
    worker?.credentials.edit_orders_of_other_employees;

  if (!canEdit) {
    return;
  }

  if (worker) {
    const batch = createBatch();
    const oldItem = order.items[item.id];

    if (!oldItem) {
      return;
    }

    const newItem: OrderItem = {
      ...item,
      cost: getOrderItemCost({
        components: dish.components,
        variation: item.variation,
        modifications: item.modifications,
        productsMap,
        semiProductsMap,
      }),
    };

    const newItems = { ...order.items, [newItem.id]: newItem };
    const isVariationChanged =
      oldItem.variation && newItem.variation && oldItem.variation.name !== newItem.variation.name;
    const addedModifications = newItem.modifications
      ? newItem.modifications.filter((mod) => !(oldItem.modifications || []).find((oldMod) => oldMod.name === mod.name))
      : [];

    const removedModifications = oldItem.modifications
      ? oldItem.modifications.filter((oldMod) => !(newItem.modifications || []).find((mod) => oldMod.name === mod.name))
      : [];

    const selfOrderPoint = order.selfOrderPointId ? selfOrderPointsMap[order.selfOrderPointId] : null;

    const updatedOrderLog: OrderLog = {
      time: Date.now(),
      workerId: worker.id,
      workerName: worker.name,
      type: 'changeItem',
      dishName: dish.name,
      state: {
        workerId: order.workerId,
        workerName: order.workerName,
        tableId: order.tableId,
        tableName: order.tableName,
        clientId: order.clientId || '',
        clientName: order.clientName || '',
        discount: order.discount || 0,
        items: newItems,
        paid: null,
        status: order.status || null,
        delivery: order.delivery || null,
      },
    };

    batch.update<Order>(`cafes/${cafeSlug}/orders/${order.id}`, {
      [`items.${newItem.id}`]: newItem as unknown as FieldValue,
    });

    batch.set<OrderLog>(`cafes/${cafeSlug}/orders/${order?.id}/logs/${uniqid()}`, updatedOrderLog);

    if (newItem.status === 'complete' && !!order.paid) {
      batch.update<Order>(`cafes/${cafeSlug}/orders/${order?.id}`, {
        closed: newItem.status === 'complete' && canClose({ ...order, items: newItems }),
      });
    }

    if (addedModifications.length || removedModifications.length) {
      const oldComponents = removedModifications.reduce<DishComponent[]>(
        (acc, mod) => [...acc, ...mod.components.map((c) => ({ ...c, qnt: -c.qnt }))],
        []
      );
      const newComponents = addedModifications.reduce<DishComponent[]>((acc, mod) => [...acc, ...mod.components], []);
      const components = [...oldComponents, ...newComponents];
      const storesToUpdate = selfOrderPoint?.storeId
        ? [selfOrderPoint.storeId]
        : uniq(components.map((c) => c.store)).filter(isNotNull);

      storesToUpdate.forEach((s) => {
        const diff: Record<string, number> = {};

        components.forEach((c) => {
          if (c.store === s || selfOrderPoint?.storeId) {
            diff[c.id] = -c.qnt;
          }
        });

        const updates: Record<`residues.${string}`, FieldValue> = Object.entries(diff).reduce(
          (acc, [id, qnt]) => ({ ...acc, [`residues.${id}`]: increment(qnt) }),
          {}
        );

        batch.update<Store>(`cafes/${cafeSlug}/stores/${s}`, updates);

        batch.set<StoreLog>(`cafes/${cafeSlug}/storeLogs/${uniqid()}`, {
          time: Date.now(),
          workerId: worker.id,
          workerName: worker.name,
          storeId: s,
          type: 'changeOrderItem',
          diff,
          info: {
            type: 'changeOrderItem',
            number: order.number,
            id: order.id,
            dishName: dish.name,
            dishId: dish.id,
          },
        });
      });
    }

    if (addedModifications.length || removedModifications.length) {
      const oldComponents = removedModifications.reduce<DishComponent[]>(
        (acc, mod) => [...acc, ...mod.components.map((c) => ({ ...c, qnt: -c.qnt }))],
        []
      );
      const newComponents = addedModifications.reduce<DishComponent[]>((acc, mod) => [...acc, ...mod.components], []);
      const components = [...oldComponents, ...newComponents];
      const storesToUpdate = selfOrderPoint?.storeId
        ? [selfOrderPoint.storeId]
        : uniq(components.map((c) => c.store)).filter(isNotNull);

      storesToUpdate.forEach((s) => {
        const diff: Record<string, number> = {};

        components.forEach((c) => {
          if (c.store === s || selfOrderPoint?.storeId) {
            diff[c.id] = -c.qnt;
          }
        });

        const updates: Record<`residues.${string}`, FieldValue> = Object.entries(diff).reduce(
          (acc, [id, qnt]) => ({ ...acc, [`residues.${id}`]: increment(qnt) }),
          {}
        );

        batch.update<Store>(`cafes/${cafeSlug}/stores/${s}`, updates);

        batch.set<StoreLog>(`cafes/${cafeSlug}/storeLogs/${uniqid()}`, {
          time: Date.now(),
          workerId: worker.id,
          workerName: worker.name,
          storeId: s,
          type: 'changeOrderItem',
          diff,
          info: {
            type: 'changeOrderItem',
            number: order.number,
            id: order.id,
            dishName: dish.name,
            dishId: dish.id,
          },
        });
      });
    }

    if (isVariationChanged) {
      const oldComponents = (oldItem.variation?.components || []).map((c) => ({ ...c, qnt: -c.qnt }));
      const components = concatComponents(oldComponents, newItem.variation?.components || []);
      const storesToUpdate = selfOrderPoint?.storeId
        ? [selfOrderPoint.storeId]
        : uniq(components.map((c) => c.store)).filter(isNotNull);

      storesToUpdate.forEach((s) => {
        const diff: Record<string, number> = {};
        const updates: Record<`residues.${string}`, FieldValue> = {};

        components.forEach((c) => {
          if (c.store === s || selfOrderPoint?.storeId) {
            updates[`residues.${c.id}`] = increment(-c.qnt);
            diff[c.id] = -c.qnt;
          }
        });

        batch.update<Store>(`cafes/${cafeSlug}/stores/${s}`, updates);

        batch.set<StoreLog>(`cafes/${cafeSlug}/storeLogs/${uniqid()}`, {
          time: Date.now(),
          workerId: worker.id,
          workerName: worker.name,
          storeId: s,
          type: 'changeOrderItem',
          diff,
          info: {
            type: 'changeOrderItem',
            number: order.number,
            id: order.id,
            dishName: dish.name,
            dishId: dish.id,
          },
        });
      });
    }

    await batch.commit();
  }
};
